import {
  Maximize,
  Minimize,
  Pause,
  Play,
  Volume2,
  VolumeX,
} from 'lucide-react';
import * as React from 'react';

import { cn } from '@/utils';

import { useFullscreen } from './use-fullscreen';
import { useHLS } from './use-hls';
import { useVideoProgress } from './use-video-progress';
import { formatTime } from './utils';

export interface VideoPlayerProps
  extends Omit<React.VideoHTMLAttributes<HTMLVideoElement>, 'onTimeUpdate'> {
  src: string;
  poster?: string;
  className?: string;
  jumpTo?: number;
  onPlay?: () => void;
  onPause?: () => void;
  onEnded?: () => void;
  onTimeUpdate?: (currentTime: number, duration: number) => void;
}

const VideoPlayer = React.forwardRef<HTMLVideoElement, VideoPlayerProps>(
  (
    {
      src,
      poster,
      className,
      jumpTo,
      onPlay,
      onPause,
      onEnded,
      onTimeUpdate,
      ...props
    },
    ref
  ) => {
    const [isPlaying, setIsPlaying] = React.useState(false);
    const [duration, setDuration] = React.useState(0);
    const [volume, setVolume] = React.useState(1);
    const [isMuted, setIsMuted] = React.useState(false);
    const [showControls, setShowControls] = React.useState(true);

    const videoRef = React.useRef<HTMLVideoElement>(null);
    const containerRef = React.useRef<HTMLDivElement>(null);
    const progressBarRef = React.useRef<HTMLButtonElement>(null);
    const controlsTimeoutRef = React.useRef<ReturnType<
      typeof setTimeout
    > | null>(null);

    // Combine refs
    React.useImperativeHandle(ref, () => videoRef.current!);

    // Custom hooks
    useHLS(videoRef, src);
    const { isFullscreen, toggleFullscreen } = useFullscreen(
      videoRef,
      containerRef
    );
    const {
      currentTime,
      handleTimeUpdate,
      handleProgressClick,
      handleMouseDown,
    } = useVideoProgress({
      videoRef,
      progressBarRef,
      duration,
      isPlaying,
      onTimeUpdate,
    });

    const handlePlayPause = () => {
      if (!videoRef.current) return;

      if (isPlaying) {
        videoRef.current.pause();
      } else {
        void videoRef.current.play();
      }
    };

    const handleLoadedMetadata = () => {
      if (!videoRef.current) return;
      setDuration(videoRef.current.duration);
    };

    const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newVolume = Number.parseFloat(e.target.value);
      setVolume(newVolume);
      if (videoRef.current) {
        videoRef.current.volume = newVolume;
      }
      setIsMuted(newVolume === 0);
    };

    const toggleMute = () => {
      if (!videoRef.current) return;
      if (isMuted) {
        videoRef.current.volume = volume;
        setIsMuted(false);
      } else {
        videoRef.current.volume = 0;
        setIsMuted(true);
      }
    };

    const showControlsTemporarily = () => {
      setShowControls(true);
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
      controlsTimeoutRef.current = setTimeout(() => {
        if (isPlaying) {
          setShowControls(false);
        }
      }, 3000);
    };

    // Handle jumpTo prop
    React.useEffect(() => {
      if (jumpTo !== undefined && videoRef.current && duration > 0) {
        const timeToJump = Math.max(0, Math.min(jumpTo, duration));
        videoRef.current.currentTime = timeToJump;
      }
    }, [jumpTo, duration]);

    // Cleanup timeout on unmount
    React.useEffect(() => {
      return () => {
        const timeoutId = controlsTimeoutRef.current;
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
      };
    }, []);

    return (
      <div
        ref={containerRef}
        className={cn(
          'relative bg-black rounded-lg overflow-hidden group',
          isFullscreen && 'rounded-none',
          className
        )}
        onMouseMove={showControlsTemporarily}
        onMouseLeave={() => isPlaying && setShowControls(false)}
      >
        <video
          ref={videoRef}
          poster={poster}
          className="w-full h-full object-contain"
          crossOrigin="use-credentials"
          playsInline
          preload="metadata"
          onPlay={() => {
            setIsPlaying(true);
            onPlay?.();
          }}
          onPause={() => {
            setIsPlaying(false);
            setShowControls(true);
            onPause?.();
          }}
          onEnded={() => {
            setIsPlaying(false);
            setShowControls(true);
            onEnded?.();
          }}
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          {...props}
        >
          <track kind="captions" />
        </video>

        {/* Play button overlay (when paused) */}
        <button
          className={cn(
            'absolute inset-0 flex items-center justify-center bg-transparent border-none transition-opacity pb-14 md:pb-2',
            showControls ? 'opacity-100' : 'opacity-0'
          )}
          onClick={handlePlayPause}
          aria-label={isPlaying ? 'Pause video' : 'Play video'}
        >
          <div className="bg-black/50 rounded-full p-2 md:p-4 hover:bg-black/70 transition-colors">
            {isPlaying ? (
              <Pause className="md:h-12 md:w-12 h-8 w-8 text-white fill-white" />
            ) : (
              <Play className="md:h-12 md:w-12 h-8 w-8 text-white fill-white" />
            )}
          </div>
        </button>

        {/* Controls */}
        <div
          className={cn(
            'absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2.5 md:p-6 transition-opacity duration-300',
            showControls ? 'opacity-100' : 'opacity-0'
          )}
        >
          {/* Progress bar */}
          <button
            ref={progressBarRef}
            className="w-full h-1 bg-white/30 rounded-full mb-4 hover:h-2 transition-all border-none p-0 relative group/seekbar"
            onClick={handleProgressClick}
            onMouseDown={handleMouseDown}
            aria-label="Seek video"
          >
            <div
              className="h-full bg-blue-500 rounded-full pointer-events-none relative"
              style={{ width: `${(currentTime / duration) * 100}%` }}
            >
              {/* Seekbar handle */}
              <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 bg-blue-500 rounded-full shadow-lg transition-transform group-hover/seekbar:scale-150" />
            </div>
          </button>

          {/* Control buttons */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {/* Play/Pause */}
              <button
                onClick={handlePlayPause}
                className="text-white hover:text-blue-400 transition-colors"
                aria-label={isPlaying ? 'Pause video' : 'Play video'}
              >
                {isPlaying ? (
                  <Pause className="h-5 w-5" />
                ) : (
                  <Play className="h-5 w-5" />
                )}
              </button>

              {/* Volume */}
              <div className="flex items-center space-x-2">
                <button
                  onClick={toggleMute}
                  className="text-white hover:text-blue-400 transition-colors"
                  aria-label={isMuted ? 'Unmute video' : 'Mute video'}
                >
                  {isMuted || volume === 0 ? (
                    <VolumeX className="h-5 w-5" />
                  ) : (
                    <Volume2 className="h-5 w-5" />
                  )}
                </button>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={isMuted ? 0 : volume}
                  onChange={handleVolumeChange}
                  className="w-20 h-1 bg-white/30 rounded-lg appearance-none cursor-pointer slider"
                />
              </div>

              {/* Time */}
              <div className="text-white md:text-sm text-[10px] font-mono">
                {formatTime(currentTime)} / {formatTime(duration)}
              </div>
            </div>

            {/* Fullscreen */}
            <button
              onClick={toggleFullscreen}
              className="text-white hover:text-blue-400 transition-colors"
              aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
            >
              {isFullscreen ? (
                <Minimize className="h-5 w-5" />
              ) : (
                <Maximize className="h-5 w-5" />
              )}
            </button>
          </div>
        </div>

        <style>{`
          .slider::-webkit-slider-thumb {
            appearance: none;
            height: 12px;
            width: 12px;
            border-radius: 50%;
            background: #3b82f6;
            cursor: pointer;
          }
          .slider::-moz-range-thumb {
            height: 12px;
            width: 12px;
            border-radius: 50%;
            background: #3b82f6;
            cursor: pointer;
            border: none;
          }
        `}</style>
      </div>
    );
  }
);

VideoPlayer.displayName = 'VideoPlayer';

export default VideoPlayer;
